/* ------------------------------------------------------------------
  File: Button.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable button component for the Accend Holdings website.
//   - Supports native button actions, React Router navigation, and external links.
//   - Supports variants, sizes, icons, disabled state, and full-width behavior.
//   - Uses the acn-button namespace to avoid conflicts with Bootstrap.
//   - Designed to work with the Accend design system tokens, theme, and utilities.

/**
 * Button
 *
 * A reusable UI button component that can render as:
 * - a native <button> when no `to` or `href` is provided
 * - a React Router <Link> when `to` is provided
 * - a standard <a> link when `href` is provided
 *
 * @param {React.ReactNode} children
 * The visible button label or content.
 *
 * @param {"primary" | "secondary" | "outline" | "ghost" | "link"} [variant="primary"]
 * Controls the visual style of the button.
 *
 * @param {"sm" | "md" | "lg"} [size="md"]
 * Controls the button size.
 *
 * @param {string} [to]
 * Internal route path for React Router navigation.
 *
 * @param {string} [href]
 * External URL or standard anchor link.
 *
 * @param {"button" | "submit" | "reset"} [type="button"]
 * Native button type. Used only when rendering a <button>.
 *
 * @param {boolean} [disabled=false]
 * Disables the button. When disabled, link navigation is prevented by rendering a native disabled button.
 *
 * @param {boolean} [fullWidth=false]
 * Makes the button take the full width of its parent container.
 *
 * @param {React.ReactNode} [icon]
 * Optional icon displayed beside the label.
 *
 * @param {"left" | "right"} [iconPosition="right"]
 * Controls whether the icon appears before or after the label.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 *
 * @param {Function} [onClick]
 * Click handler for button actions.
 *
 * @param {string} [ariaLabel]
 * Accessible label for icon-only or context-specific buttons.
 *
 * @param {string} [target]
 * Anchor target for external links. Example: "_blank".
 *
 * @param {string} [rel]
 * Anchor rel attribute. Automatically set to "noopener noreferrer" when target is "_blank".
 */

import { Link } from "react-router-dom";
import "./Button.css";

const Button = ({
  children,
  variant = "primary",
  size = "md",
  to,
  href,
  type = "button",
  disabled = false,
  fullWidth = false,
  icon,
  iconPosition = "right",
  className = "",
  onClick,
  ariaLabel,
  target,
  rel,
  ...props
}) => {
  const buttonClasses = [
    "acn-button",
    `acn-button--${variant}`,
    `acn-button--${size}`,
    fullWidth ? "acn-button--full" : "",
    icon ? "acn-button--with-icon" : "",
    icon && iconPosition === "left" ? "acn-button--icon-left" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {icon && iconPosition === "left" && (
        <span className="acn-button__icon" aria-hidden="true">
          {icon}
        </span>
      )}

      <span className="acn-button__label">{children}</span>

      {icon && iconPosition === "right" && (
        <span className="acn-button__icon" aria-hidden="true">
          {icon}
        </span>
      )}
    </>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={buttonClasses} aria-label={ariaLabel} {...props}>
        {content}
      </Link>
    );
  }

  if (href && !disabled) {
    return (
      <a
        href={href}
        className={buttonClasses}
        aria-label={ariaLabel}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : rel}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;